import dayjs, { type Dayjs } from 'dayjs'
import type { ListIterateeCustom } from 'lodash'
import { filter, mapKeys } from 'lodash'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import useFormatters from '~/data/formatters'
import roundToNearestMinutes from '~/data/utils/time'
import { useUserPrefs } from '~/hooks/useUserPrefs'

import type { UsePollingOptions } from './polling'
import type { MaybeDateRange, ValidatedTime } from './types'

export type ValidatedRange = {
  from: ValidatedTime
  to: ValidatedTime
  range: {
    valid: boolean
    errors: string[]
  }
}

type ValidateDateRangeOptions = {
  maxDays?: number
  minDaysAgo?: number
}

type ValidTimeMode = 'START' | 'END'

/**
 * Validates the date range and returns a rounded version of the dates.
 */
export function useValidatedDateRange(
  { from, to }: MaybeDateRange = {},
  { maxDays, minDaysAgo }: ValidateDateRangeOptions = {}
) {
  const t = useTranslations('data.time')
  const format = useFormatters()

  const validatedFrom = useMemo(() => validateTime(from, 'START'), [from])
  const validatedTo = useMemo(() => validateTime(to, 'END'), [to])
  const { tzDayjs } = useUserPrefs()

  return useMemo<ValidatedRange>(() => {
    let rangeValid = true
    const errors = []

    if (!validatedFrom.valid) {
      rangeValid = false
      errors.push(t('range_errors_invalidStartDate'))
    }

    if (!validatedTo.valid) {
      rangeValid = false
      errors.push(t('range_errors_invalidEndDate'))
    }

    // If they are both valid, check the range
    if (validatedFrom.valid && validatedTo.valid) {
      const toTime = dayjs(validatedTo.time)
      const fromTime = dayjs(validatedFrom.time)

      if (toTime.isSameOrBefore(fromTime)) {
        rangeValid = false
        errors.push(t('range_errors_endBeforeStart'))
      }

      if (maxDays && toTime.diff(fromTime, 'minutes') > maxDays * 24 * 60) {
        rangeValid = false
        errors.push(t('range_errors_maxDaysExceeded', { maxDays }))
      }

      if (minDaysAgo) {
        const minDate = roundToNearestMinutes(tzDayjs().subtract(minDaysAgo, 'days').add(1, 'minutes'), 5, 'up')
        if (fromTime.isBefore(minDate) || toTime.isBefore(minDate)) {
          rangeValid = false
          errors.push(t('range_errors_minDaysAgoRequired', { minDate: format.dateTime(minDate, 'dateDotTime') }))
        }
      }
    }

    return {
      from: validatedFrom,
      to: validatedTo,
      range: { valid: rangeValid, errors }
    }
  }, [validatedFrom, validatedTo, maxDays, minDaysAgo, format, tzDayjs, t])
}

function validateTime(time?: Dayjs, timeMode: ValidTimeMode = 'START'): ValidatedTime {
  const isValid = time?.isValid() ?? false
  const validTime = timeMode === 'START' ? time?.startOf('minute').toISOString() : time?.endOf('minute').toISOString()
  return {
    valid: isValid,
    time: (isValid && validTime) || ''
  }
}

/**
 * Combines the skip option from the hook with the skip option from the caller. If
 * either the hook or the caller wants to skip, then the query will be skipped. This
 * is useful for omitting boilerplate code in the caller, such as `skip: !siteId`.
 */
export function combineSkip(ownSkip: boolean, options?: UsePollingOptions) {
  const optionsSkip = options?.skip ?? false
  return { ...options, skip: ownSkip || optionsSkip }
}

/**
 * Merge cursor follow-up pages onto a first page. First-page rows win on id collision
 * so a mid-walk refresh cannot duplicate or replace fresher page-one data.
 */
export function mergeByIdPreferFirst<T extends { id: string }>(firstItems: T[], extraPages: T[]): T[] {
  const firstIds = new Set(firstItems.map((row) => row.id))
  return [...firstItems, ...extraPages.filter((row) => !firstIds.has(row.id))]
}

type TransformedDictOptions = {
  caseInsensitive?: boolean
  prefix?: string | string[]
  separator?: string
}

/**
 * Transforms the input data to a dictionary that is normalized based on case, prefix, and separator.
 * This is useful for looking up data that has a canonical key, but may be stored with variations.
 * For example - this is used for normalizing auth tags for lookups that are insensitive
 * to variants like 'AaBbCc' vs 'aabbcc' vs 'VID:AABBCC' vs 'AA:BB:CC'
 *
 * @param data The input data to be transformed to a normalized dictionary
 */
export function useTransformedDict<T>(data: Record<string, T>, options?: TransformedDictOptions) {
  // Extract from the object to avoid re-triggering `useMemo` when the caller passes in the same
  // non-memoized object
  const { caseInsensitive, prefix, separator } = options ?? {}

  return useMemo(() => {
    const transformed = mapKeys(data, (_, key) => applyOptions(key, { caseInsensitive, prefix, separator }))

    return new Proxy(transformed, {
      get: function (target, prop) {
        // Transform the lookup key based on options
        return target[applyOptions(prop.toString(), { caseInsensitive, prefix, separator })]
      }
    })
  }, [data, caseInsensitive, prefix, separator])
}

type NormalizedLookupOptions<T> = TransformedDictOptions & {
  // Function to get the key to normalize from an entity
  getKey: (value: T) => string | undefined
}

type NormalizedLookupResult<T> = {
  // The normalized key -> array of entities mapping
  normalizedDict: Record<string, T[]>
  // The lookup function that takes a key and context
  lookup: (key: string, context?: ListIterateeCustom<T, boolean>) => T | undefined
}

/**
 * Creates a normalized lookup that handles multiple values that normalize to the same key.
 * Uses context to disambiguate between multiple matches.
 *
 * @param entities Array of entities to index
 * @param options Options for normalization, key mapping, and context filtering
 * @returns A lookup function and supporting data structures
 */
export function useNormalizedLookup<T>(entities: T[], options: NormalizedLookupOptions<T>): NormalizedLookupResult<T> {
  const { caseInsensitive, prefix, getKey, separator } = options

  return useMemo(() => {
    // Build the normalized key -> entities mapping
    const normalizedDict: Record<string, T[]> = {}

    entities.forEach((entity) => {
      const originalKey = getKey(entity)
      if (!originalKey) return

      const normalizedKey = applyOptions(originalKey, { caseInsensitive, prefix, separator })

      if (!normalizedDict[normalizedKey]) {
        normalizedDict[normalizedKey] = []
      }
      normalizedDict[normalizedKey].push(entity)
    })

    // Create the lookup function
    const lookup = (key: string, context?: ListIterateeCustom<T, boolean>): T | undefined => {
      const normalizedKey = applyOptions(key, { caseInsensitive, prefix, separator })
      const matchingEntities = normalizedDict[normalizedKey]

      if (!matchingEntities?.length) return undefined

      const filtered = filter(matchingEntities, context)

      // Return the first match here even if there are multiple.
      return filtered[0]
    }

    return { normalizedDict, lookup }
  }, [entities, caseInsensitive, prefix, getKey, separator])
}

/**
 * Applies case-insensitivity and prefix-stripping transformations to a key.
 * This is useful for normalizing keys for lookups.
 *
 * @param key The key to transform
 * @param options Transformation options
 * @returns The transformed key
 */
export function applyOptions(key: string, options: TransformedDictOptions) {
  const { caseInsensitive, prefix, separator } = options
  const desensitized = caseInsensitive ? key.toUpperCase() : key

  let stripped = desensitized
  const stripPrefix = (value: string, candidate?: string) => {
    if (!candidate) return value
    const normalizedPrefix = caseInsensitive ? candidate.toUpperCase() : candidate
    return value.startsWith(normalizedPrefix) ? value.slice(normalizedPrefix.length) : value
  }

  if (Array.isArray(prefix)) {
    for (const p of prefix) {
      const next = stripPrefix(stripped, p)
      if (next !== stripped) {
        stripped = next
        break
      }
    }
  } else if (typeof prefix === 'string' && prefix.length > 0) {
    stripped = stripPrefix(stripped, prefix)
  }

  if (!separator) {
    return stripped
  }

  return stripped.split(separator).join('')
}
